import React, { createContext, useState, useContext, useCallback } from "react";
import { TOAST_CLASSES } from "../utils/constants";
import ToastComponent from "../components/Atoms/Toasts/ToastComponent";
import SimpleModal from "../components/Atoms/Modals/SimpleModal";
import CustomButton from "../components/Atoms/Buttons/CustomButton";
import { Backdrop, CircularProgress } from "@mui/material";

const DashboardContext = createContext();

export const DashboardProvider = ({ children }) => {
  const [toastMessage, setToastMessage] = useState("");
  const [toastClsNames, setToastClsNames] = useState(TOAST_CLASSES?.SUCCESS);
  const [toastOpen, setToastOpen] = useState(false);

  const [modalDetails, setModalDetails] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [uploadDrawerOpen, setUploadDrawerOpen] = useState(false);

  const [showLoader, setShowLoader] = useState(false);

  const changeUploadDrawerState = (bool) => {
    setUploadDrawerOpen(bool);
  }

  const showConfirmationModal = useCallback((modalDetailsParam) => {
    setModalDetails(modalDetailsParam);
    setShowModal(true);
  }, []);

  // Show backdrop spinner
  const showSpinner = useCallback((isOpen) => {
    setShowLoader(isOpen);
  }, []);

  // Show toast message
  const showToastMessage = useCallback(
    (message, clsNames = TOAST_CLASSES?.SUCCESS) => {
      setToastMessage(message);
      setToastClsNames(clsNames);
      setToastOpen(true);
    },
    []
  );

  const value = {
    uploadDrawerOpen,
    changeUploadDrawerState,
    showToastMessage,
    showConfirmationModal,
    showSpinner,
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}

      <ToastComponent
        isToastOpen={toastOpen}
        setIsToastOpen={setToastOpen}
        message={toastMessage}
        containerClsNames={toastClsNames}
      />

      <SimpleModal
        show={showModal}
        setShow={setShowModal}
        modalClsNames="mas-modal-container"
        closeIcon={true}
      >
        <div className="mas-modal-title">
          {modalDetails?.title || "Confirmation"}
        </div>
        <div className="mas-modal-body">
          {modalDetails?.body || "Are you sure you want to proceed?"}
        </div>
        <div className="d-flex justify-content-end gap-2 wd-100">
          <CustomButton
            clsNames="mas-secondary-btn-3 mas-btn-sml"
            onClickFn={(e) => {
              e?.stopPropagation();
              setShowModal(false);

              if (modalDetails?.onCancel) {
                modalDetails.onCancel(e);
              } else {
                setShowModal(false);
              }
            }}
            label={modalDetails?.onCancelLabel || "Cancel"}
            styles={{ borderRadius: "4px" }}
          />
          <CustomButton
            clsNames="mas-primary-btn mas-btn-sml"
            onClickFn={(e) => {
              e?.stopPropagation();

              setShowModal(false);
              if (modalDetails?.onOk) {
                modalDetails.onOk(e);
              } else {
                setShowModal(false);
              }
            }}
            label={modalDetails?.onOkLabel || "Ok"}
            styles={{ borderRadius: "4px" }}
          />
        </div>
      </SimpleModal>

      <Backdrop
        sx={{
          color: "var(--db-primary-white)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          height: "134vh !important",
        }}
        open={showLoader}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </DashboardContext.Provider>
  );
};

// Custom hook for using the context
export const useDashboard = () => {
  const context = useContext(DashboardContext);

  if (!context) {
    throw new Error("useDashboard must be used within a DashboardProvider");
  }

  return context;
};

export default DashboardContext;